// REQ-13: al cambiar la ancla (equipo o kit) o si el wizard marca algo sin revalidar, vuelve a aplicar.
import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import {
    evaluationKey,
    useSalePromotionEvaluation,
    useStoreSalePromotions,
} from '../api/useWizardPromotions';
import { wizardPromotionsClient } from '../api/wizardPromotions.client';
import { withCustomerPhone } from '../lib/customerPhone';
import {
    contextNeedsRevalidation,
    reapplyOrRelease,
    reapplySelection,
    seedFromSession,
    type AppliedSelection,
} from '../lib/applySelection';
import { promotionErrorCopy } from '../lib/promotionErrorCopy';
import { rememberEvaluatedNames } from '../lib/promotionNames';
import { rebaseOnAnchor, stalePromotionIds } from '../lib/promotionSelection';
import {
    recordDiscountCodeBlock,
    sessionPromotionState,
    storePort,
    usePromotionSelectionStore,
    useSessionPromotionState,
} from '../store/promotionSelectionStore';
import { notifyDroppedPromotions } from './droppedPromotionNotice';
import { finishWrite, startWrite } from './settleAfterWrites';

export interface ReapplyOptions {
    sessionId: string;
    context: AnchorSlotContext;
}

export function anchorKeyOf({ anchorLine }: Pick<AnchorSlotContext, 'anchorLine'>): string {
    return [anchorLine.lineId, anchorLine.sku].join('|');
}

export function useReapplyOnSaleChange({ sessionId, context }: ReapplyOptions) {
    const qc = useQueryClient();
    const api = withCustomerPhone(wizardPromotionsClient, sessionId, qc);
    const patch = usePromotionSelectionStore((s) => s.patch);
    const { synced, selection } = useSessionPromotionState(sessionId);
    const { data: evaluation, isPlaceholderData } = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    const refresh = useRef(context.refreshSession);
    const contextRef = useRef(context);
    const pending = contextNeedsRevalidation(context);
    const mutation = useMutation<AppliedSelection | null>({
        // Con líneas por revalidar algo tiene que escribirse: sin nada elegido se suelta, así el silo
        // quita la marca y «Continuar» no se queda esperando.
        mutationFn: () =>
            pending
                ? reapplyOrRelease(sessionId, storePort(sessionId), api)
                : reapplySelection(sessionId, storePort(sessionId), api),
        onMutate: () => {
            startWrite(sessionId);
        },
        onSuccess: (applied) => {
            patch(sessionId, { reapplyError: null });
            notifyDroppedPromotions(sessionId, applied);
        },
        onError: (error) => {
            recordDiscountCodeBlock(sessionId, error);
        },
        onSettled: () => {
            finishWrite(sessionId, true, () => {
                void qc.invalidateQueries({ queryKey: evaluationKey(sessionId) });
                void refresh.current();
            });
        },
    });

    const key = anchorKeyOf(context);
    const mutate = useRef(mutation.mutate);
    useEffect(() => {
        refresh.current = context.refreshSession;
        contextRef.current = context;
        mutate.current = mutation.mutate;
    });

    // Sin conocer la sesión no se reaplica: el PUT borraría lo que ya tiene apartado.
    useEffect(() => {
        if (!synced) return;
        const previous = sessionPromotionState(sessionId).saleKey;
        const anchorChanged = previous !== undefined && previous !== key;
        if (previous !== key) patch(sessionId, { saleKey: key });
        if (anchorChanged) {
            const { anchorLine, extraLines = [] } = contextRef.current;
            const { selection: base } = sessionPromotionState(sessionId);
            const selection = rebaseOnAnchor(
                base,
                anchorLine.lineId,
                extraLines.map((l) => l.lineId),
                seedFromSession(contextRef.current),
            );
            patch(sessionId, { selection });
        }
        if (anchorChanged || pending) mutate.current();
    }, [sessionId, key, pending, patch, synced]);

    useEffect(() => {
        if (evaluation) rememberEvaluatedNames(sessionId, evaluation);
    }, [sessionId, evaluation]);

    // Una promo apartada que Promociones ya no lista (pausada) se suelta con la misma reevaluación.
    // Una que la tienda sigue listando (p. ej. de accesorio sin monto en evaluate) no está pausada.
    const listed = new Set((store?.items ?? []).map((p) => p.promotionId));
    const stale =
        // La evaluación de la venta anterior no basta para soltar una promo.
        synced && evaluation && !isPlaceholderData
            ? stalePromotionIds(selection, evaluation)
                  .filter((id) => !listed.has(id))
                  .join('|')
            : '';
    const releasedStale = useRef('');
    useEffect(() => {
        if (!stale || releasedStale.current === stale) return;
        releasedStale.current = stale;
        mutate.current();
    }, [stale]);

    return {
        isReapplying: mutation.isPending,
        error: mutation.error ? promotionErrorCopy(mutation.error) : null,
        /** Vuelve a aplicar a mano tras un error. */
        retry: () => {
            mutate.current();
        },
    };
}
